import { apiService } from './api'
import { useAuthStore } from '@/stores/auth'

export const productsApi = {
  // Get all products for current business
  getProducts: async (params = {}) => {
    try {
      const authStore = useAuthStore()
      const businessId = authStore.businessId
      
      console.log('Products API Debug:', {
        isAuthenticated: authStore.isAuthenticated,
        user: authStore.user,
        business: authStore.business,
        businessId: businessId,
        userBusinessId: authStore.user?.business_id
      })
      
      if (!businessId) {
        console.error('No business ID available:', {
          authStore: authStore,
          businessValue: authStore.business,
          userValue: authStore.user
        })
        throw new Error('No business ID available')
      }
      
      const queryParams = new URLSearchParams()
      
      // Always include business_id
      queryParams.append('business_id', businessId)
      
      // Add optional query parameters
      if (params.search) queryParams.append('search', params.search)
      if (params.category) queryParams.append('category', params.category)
      if (params.barcode) queryParams.append('barcode', params.barcode)
      if (params.page) queryParams.append('page', params.page)
      if (params.limit) queryParams.append('limit', params.limit)
      
      const query = queryParams.toString()
      const url = `/products?${query}`
      
      console.log('Making products API call:', url)
      
      return await apiService.get(url)
    } catch (error) {
      console.error('Error fetching products:', error)
      throw error
    }
  },

  // Get single product by ID
  getProduct: async (productId) => {
    try {
      return await apiService.get(`/products/${productId}`)
    } catch (error) {
      console.error('Error fetching product:', error)
      throw error
    }
  },

  // Get product by barcode
  getProductByBarcode: async (barcode) => {
    try {
      return await apiService.get(`/products/barcode/${barcode}`)
    } catch (error) {
      console.error('Error fetching product by barcode:', error)
      throw error
    }
  },

  // Create new product
  createProduct: async (productData) => {
    try {
      const authStore = useAuthStore()
      const businessId = authStore.businessId
      
      if (!businessId) {
        throw new Error('No business ID available')
      }
      
      // Include business_id in the product data
      const payload = {
        ...productData,
        business_id: businessId
      }
      
      return await apiService.post('/products', payload)
    } catch (error) {
      console.error('Error creating product:', error)
      throw error
    }
  },

  // Update existing product
  updateProduct: async (productId, productData) => {
    try {
      return await apiService.put(`/products/${productId}`, productData)
    } catch (error) {
      console.error('Error updating product:', error)
      throw error
    }
  },

  // Delete product
  deleteProduct: async (productId) => {
    try {
      return await apiService.delete(`/products/${productId}`)
    } catch (error) {
      console.error('Error deleting product:', error)
      throw error
    }
  },

  // Lookup product by short code or barcode
  lookupProduct: async (code) => {
    try {
      return await apiService.get(`/products/lookup/${code}`)
    } catch (error) {
      console.error('Error looking up product:', error)
      throw error
    }
  },

  // Import products from file
  importProducts: async (file, options = {}) => {
    try {
      const authStore = useAuthStore()
      const businessId = authStore.businessId
      
      if (!businessId) {
        throw new Error('No business ID available')
      }

      const formData = new FormData()
      formData.append('file', file)
      formData.append('business_id', businessId)
      formData.append('options', JSON.stringify(options))

      const response = await fetch(`${import.meta.env.VITE_API_URL}/products/bulk-import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authStore.token}`
        },
        body: formData
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Import failed')
      }

      return await response.json()
    } catch (error) {
      console.error('Error importing products:', error)
      throw error
    }
  }
}
